/**
 * The headline tabs of a diverging-likert plot (decision 20, RND-2753; plots
 * plan SS-1 / SS-2 / P2): what a row prints and draws on the active view.
 *
 * Display only. Every figure is Python's (`figure_plots.rating_stat_views`,
 * `box_segments`, `paired_changes`): the headline shares, the Top N view's
 * three pieces, the counts, the base and exclusions, and each paired change
 * with its test. Nothing here sums, subtracts or rounds a figure; a figure the
 * payload does not carry is not drawn. The bar's data never changes with the
 * tab: Average and NPS draw the stamped segments, Top N draws the same answers
 * grouped as Python stamped them.
 */

import type { RatingPolarity } from '@digital-twins/shared-types'

import { StatTabLabels, availableStatViews, resolveStatView } from '../_shared/StatTabs'
import { DivergingLikertStoredData, DivergingLikertStoredRow } from './plot-plan'

import { PlotStatView, PlotStatViews } from '@digital-twins/slide-schemas/generated/plot-types'

export type DivergingLikertChange = NonNullable<DivergingLikertStoredRow['changes']>[number]

/** What one bar piece is painted as: a band (Average / NPS) or a box (Top N). */
export type DivergingLikertPieceTone = 'favorable' | 'middle' | 'unfavorable' | 'mixed'

export interface DivergingLikertViewPiece {
  label: string
  /** The printed share, as Python rounded it. */
  value: number
  /**
   * The share the piece is drawn with: Python's unrounded `exactShare`, so the
   * pieces fill the bar. Legacy reader only: a plot stored before it draws `value`.
   */
  share: number
  count: number | null
  tone: DivergingLikertPieceTone
  /** Stamped words for the scale end this piece contains. Null on a middle piece and when no anchor was stamped. */
  anchor: string | null
}

export interface DivergingLikertHeadlineFigure {
  value: number
  format: 'percent' | 'mean' | 'nps'
  decimals: number
  label: string | null
  /**
   * A mean on a row with its own scale (W7 Q9): the scale's top, printed beside
   * the figure ("4.7 /7") so means on different scales are never read as one.
   */
  outOf?: number | null
}

export interface DivergingLikertHeadline {
  primary: DivergingLikertHeadlineFigure | null
  /** Top N only: the Bottom N share, printed smaller beneath. */
  secondary: DivergingLikertHeadlineFigure | null
}

const BOX_TONES: Record<'bottom' | 'middle' | 'top', DivergingLikertPieceTone> = {
  bottom: 'unfavorable',
  middle: 'middle',
  top: 'favorable',
}

function isFigure(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value)
}

/** The views the exhibit offers: those of its first row that carries `stats`. */
export function divergingLikertStats(data: DivergingLikertStoredData): PlotStatViews | null {
  const row = data.rows.find((candidate) => candidate.stats)
  return row?.stats ?? null
}

/** The view to draw: the requested one, else the payload's default, else Average. */
export function divergingLikertActiveView(
  data: DivergingLikertStoredData,
  requested?: PlotStatView | null,
): PlotStatView {
  const stats = divergingLikertStats(data)
  if (!stats) {
    return 'mean'
  }
  if (requested && availableStatViews(stats).includes(requested)) {
    return requested
  }
  return resolveStatView(stats, data.defaultView ?? null)
}

/**
 * The tab labels the payload carries (no English fill-in); `fallback` fills a
 * tab a stored payload names no label for (the app's own viewer-language key).
 */
export function divergingLikertTabLabels(
  data: DivergingLikertStoredData,
  fallback: StatTabLabels = {},
): StatTabLabels {
  const topBoxes = data.labels.stat_tab_top_n ?? fallback.topBoxes ?? null
  const sizes = divergingLikertBoxSizes(data)
  return {
    average: data.labels.stat_tab_average ?? fallback.average ?? null,
    // Rows on different scales with different N (W7 Q9): every N the tab covers ("Top 2 / Top 3").
    topBoxes: topBoxes && sizes.length > 1 ? sizes.map((n) => topBoxes.replace('{n}', String(n))).join(' / ') : topBoxes,
    nps: data.labels.stat_tab_nps ?? fallback.nps ?? null,
  }
}

/** The distinct Top N sizes of the rows that offer the view, smallest first. */
export function divergingLikertBoxSizes(data: DivergingLikertStoredData): Array<number> {
  const sizes = new Set<number>()
  for (const row of data.rows) {
    const size = row.stats?.boxSize
    if (typeof size === 'number') {
      sizes.add(size)
    }
  }
  return [...sizes].sort((a, b) => a - b)
}

/** A Top N piece's name: the row's own (rows on different scales, W7 Q9), else the exhibit's. */
function boxLabel(
  data: DivergingLikertStoredData,
  box: 'bottom' | 'middle' | 'top',
  row?: DivergingLikertStoredRow,
): string | null {
  return row?.boxLabels?.[box] ?? data.boxLabels?.[box] ?? null
}

/**
 * The scale a row reads on when it carries its own (W7 Q9), as its ends
 * ("1-7", with the anchors when stamped: "1 Not at all, 7 Very"); null on a
 * row that reads the exhibit's scale.
 */
export function divergingLikertRowScaleText(row: DivergingLikertStoredRow): string | null {
  const scale = row.scale
  if (!scale) {
    return null
  }
  const low = scale.minLabel ? `${scale.min} ${scale.minLabel}` : String(scale.min)
  const high = scale.maxLabel ? `${scale.max} ${scale.maxLabel}` : String(scale.max)
  return scale.minLabel || scale.maxLabel ? `${low}, ${high}` : `${scale.min}-${scale.max}`
}

/**
 * The chrome word naming a row's stamped `stat` on a payload stored before the
 * headline tabs (Python's words; the mean has none and takes the Average tab's).
 */
const STORED_STAT_LABEL_KEYS: Record<DivergingLikertStoredData['stat'], keyof DivergingLikertStoredData['labels'] | null> = {
  favorable_share: 'favorable',
  top_two_box: 'plot_top2_box',
  nps: 'plot_nps_score',
  promoter_share: 'plot_nps_promoters',
  mean: null,
}

/** A payload stored before the tabs: its one stamped `stat`, as printed then. */
function storedHeadline(
  row: DivergingLikertStoredRow,
  data: DivergingLikertStoredData,
  fallback: StatTabLabels,
): DivergingLikertHeadline {
  const stat = row.stat
  if (!stat || !isFigure(stat.value)) {
    return { primary: null, secondary: null }
  }
  const key = STORED_STAT_LABEL_KEYS[data.stat]
  const label = key ? data.labels[key] ?? null : data.labels.stat_tab_average ?? fallback.average ?? null
  const format = stat.format === 'percent' ? 'percent' : stat.format === 'nps' ? 'nps' : 'mean'
  return { primary: { value: stat.value, format, decimals: stat.decimals, label }, secondary: null }
}

/**
 * The row's headline on a view: Top N share (+ Bottom N beneath), mean, or NPS
 * score; a row stored without `stats` prints its one stamped `stat`.
 */
export function divergingLikertHeadline(
  row: DivergingLikertStoredRow,
  data: DivergingLikertStoredData,
  view: PlotStatView,
  fallback: StatTabLabels = {},
): DivergingLikertHeadline {
  const stats = row.stats
  if (!stats) {
    return storedHeadline(row, data, fallback)
  }
  if (view === 'topBoxes' && isFigure(stats.topBoxes)) {
    return {
      primary: { value: stats.topBoxes, format: 'percent', decimals: data.decimals, label: boxLabel(data, 'top', row) },
      secondary: isFigure(stats.bottomBoxes)
        ? { value: stats.bottomBoxes, format: 'percent', decimals: data.decimals, label: boxLabel(data, 'bottom', row) }
        : null,
    }
  }
  if (view === 'nps' && isFigure(stats.nps)) {
    return {
      primary: { value: stats.nps, format: 'nps', decimals: data.decimals, label: data.labels.plot_nps_score ?? null },
      secondary: null,
    }
  }
  if (isFigure(stats.mean)) {
    return {
      primary: {
        value: stats.mean,
        format: 'mean',
        decimals: data.meanDecimals ?? 1,
        label: data.labels.stat_tab_average ?? fallback.average ?? null,
        outOf: row.scale ? row.scale.max : null,
      },
      secondary: null,
    }
  }
  return { primary: null, secondary: null }
}

/** The scale a piece is read on: the row's own when it has one, else the exhibit's. */
function pieceScale(row: DivergingLikertStoredRow, data: DivergingLikertStoredData): DivergingLikertStoredData['scale'] {
  return row.scale ?? data.scale
}

function endAnchor(scale: DivergingLikertStoredData['scale'], end: 'min' | 'max'): string | null {
  const text = end === 'min' ? scale?.minLabel : scale?.maxLabel
  const trimmed = text?.trim()
  return trimmed ? trimmed : null
}

/**
 * The scale end a favourable or unfavourable piece sits at: the maximum on a
 * higher-is-better scale, the minimum on a lower-is-better one. A
 * middle-is-best scale has no favourable end (its favourable band is the
 * middle), so null.
 */
function favorableIsMaxOf(scale: NonNullable<DivergingLikertStoredData['scale']>): boolean | null {
  const polarity = scale.polarity ?? 'higher_is_better'
  return polarity === 'middle_is_best' ? null : polarity === 'higher_is_better'
}

/** Top is the favourable end. On a lower-is-better scale that end is the minimum. */
function boxEndAnchor(
  box: 'bottom' | 'middle' | 'top',
  scale: DivergingLikertStoredData['scale'],
): string | null {
  if (!scale || box === 'middle') {
    return null
  }
  const favorableIsMax = favorableIsMaxOf(scale)
  if (favorableIsMax === null) {
    return null
  }
  if (box === 'top') {
    return endAnchor(scale, favorableIsMax ? 'max' : 'min')
  }
  return endAnchor(scale, favorableIsMax ? 'min' : 'max')
}

function bandEndAnchor(
  band: DivergingLikertPieceTone,
  scale: DivergingLikertStoredData['scale'],
): string | null {
  if (!scale || (band !== 'favorable' && band !== 'unfavorable')) {
    return null
  }
  const favorableIsMax = favorableIsMaxOf(scale)
  if (favorableIsMax === null) {
    return null
  }
  if (band === 'favorable') {
    return endAnchor(scale, favorableIsMax ? 'max' : 'min')
  }
  return endAnchor(scale, favorableIsMax ? 'min' : 'max')
}

/** A point label ("1", "5") is that point. Anything else is not a point. */
function pointNumber(label: string): number | null {
  const trimmed = label.trim()
  if (trimmed === '') {
    return null
  }
  const value = Number(trimmed)
  if (!Number.isInteger(value) || String(value) !== trimmed) {
    return null
  }
  return value
}

function segmentAnchor(
  label: string,
  band: DivergingLikertPieceTone,
  scale: DivergingLikertStoredData['scale'],
): string | null {
  if (!scale) {
    return null
  }
  const point = pointNumber(label)
  if (point != null) {
    if (point === scale.min) {
      return endAnchor(scale, 'min')
    }
    if (point === scale.max) {
      return endAnchor(scale, 'max')
    }
    return null
  }
  if (scale.kind === 'nps') {
    return bandEndAnchor(band, scale)
  }
  return null
}

/** Label plus its scale-end words, when that piece has them. */
export function divergingLikertAnchoredLabel(label: string, anchor: string | null): string {
  if (!anchor) {
    return label
  }
  return `${label}, ${anchor}`
}

/**
 * Lightest step of a repeated tone. Still that colour, not the page: two
 * favourable points stay two greens, and a longer scale (a 1-10) takes more
 * steps up to the full tone.
 */
const TONE_SCALE_FLOOR = 0.48

/**
 * How strong each piece paints its tone. Pieces are in scale order. One piece
 * of a tone stays at full strength. Several share a ramp: the extreme of a
 * pole is full, the step nearest the middle is the floor. Neutral ramps from
 * the low end of the scale toward the high end. A lower-is-better scale flips
 * which pole is extreme. On a middle-is-best scale the unfavourable pieces sit
 * on both sides of the favourable middle: each side ramps toward its own end.
 */
export function divergingLikertPieceIntensities(
  pieces: Array<Pick<DivergingLikertViewPiece, 'tone'>>,
  polarity: RatingPolarity = 'higher_is_better',
): Array<number> {
  const firstFavorable = pieces.findIndex((piece) => piece.tone === 'favorable')
  const byGroup = new Map<string, { tone: DivergingLikertPieceTone; indexes: Array<number> }>()
  pieces.forEach((piece, index) => {
    // On a middle-is-best scale an unfavourable piece belongs to its side.
    const side = polarity === 'middle_is_best' && piece.tone === 'unfavorable' && firstFavorable >= 0
      ? (index < firstFavorable ? ':low' : ':high')
      : ''
    const key = `${piece.tone}${side}`
    const group = byGroup.get(key) ?? { tone: piece.tone, indexes: [] }
    group.indexes.push(index)
    byGroup.set(key, group)
  })
  const intensities = pieces.map(() => 1)
  for (const [key, { tone, indexes }] of byGroup) {
    if (indexes.length < 2) {
      continue
    }
    const darkAtLowEnd = key.endsWith(':low')
      || (!key.endsWith(':high') && (
        tone === 'unfavorable' ? polarity !== 'lower_is_better' : tone === 'favorable' ? polarity === 'lower_is_better' : false
      ))
    indexes.forEach((pieceIndex, rank) => {
      const step = rank / (indexes.length - 1)
      const towardHigh = darkAtLowEnd ? 1 - step : step
      intensities[pieceIndex] = TONE_SCALE_FLOOR + (1 - TONE_SCALE_FLOOR) * towardHigh
    })
  }
  return intensities
}

/** The bar pieces on a view, low end first: grouped on Top N, the stamped segments otherwise. */
export function divergingLikertViewPieces(
  row: DivergingLikertStoredRow,
  data: DivergingLikertStoredData,
  view: PlotStatView,
): Array<DivergingLikertViewPiece> {
  const scale = pieceScale(row, data)
  if (view === 'topBoxes' && row.boxSegments && row.boxSegments.length === 3) {
    return row.boxSegments.map((piece) => ({
      label: boxLabel(data, piece.box, row) ?? '',
      value: piece.value,
      share: piece.exactShare ?? piece.value,
      count: piece.count ?? null,
      tone: BOX_TONES[piece.box],
      anchor: boxEndAnchor(piece.box, scale),
    }))
  }
  return row.segments.map((segment) => ({
    label: segment.label,
    value: segment.value,
    share: segment.exactShare ?? segment.value,
    count: segment.count ?? null,
    tone: segment.band,
    anchor: segmentAnchor(segment.label, segment.band, scale),
  }))
}

function fill(template: string, values: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (match, name: string) => (name in values ? String(values[name]) : match))
}

/** "Base: 120 answering; 4 excluded" when the row states its exclusions, else "n = 120". */
export function divergingLikertBaseText(
  row: DivergingLikertStoredRow,
  data: DivergingLikertStoredData,
): string | null {
  if (row.base == null) {
    return null
  }
  const withExcluded = data.labels.plot_base_answering_excluded
  if (typeof row.excludedCount === 'number' && withExcluded) {
    return fill(withExcluded, { n: row.base, excluded: row.excludedCount })
  }
  return data.labels.plot_base_n ? fill(data.labels.plot_base_n, { n: row.base }) : null
}

/** The paired change on the active view (after row only), or null. */
export function divergingLikertChange(
  row: DivergingLikertStoredRow,
  view: PlotStatView,
): DivergingLikertChange | null {
  return row.changes?.find((change) => change.view === view) ?? null
}

export interface DivergingLikertLegendItem {
  key: string
  label: string
  tone: DivergingLikertPieceTone
  /** Scale-end words. Null when rows do not share one scale, or the entry is not an end. */
  anchor: string | null
}

/** Rows on their own scales share one legend, so that legend names no end. */
function legendScale(data: DivergingLikertStoredData): DivergingLikertStoredData['scale'] {
  if (data.rows.some((row) => row.scale)) {
    return undefined
  }
  return data.scale
}

export interface DivergingLikertScaleKey {
  /** Low end of the scale: "1 = Not at all clear", or the Top N points when that end is favourable. */
  low: string | null
  /** High end of the scale: "5 = Completely clear", or the Top N points when that end is favourable. */
  high: string | null
}

function endPhrase(number: number, anchor: string | null, points: string | null): string | null {
  if (!anchor) {
    return null
  }
  const named = `${number} = ${anchor}`
  return points ? `${points}, ${named}` : named
}

/**
 * The scale key above a survey-tab bar: both ends, in the heat-grid form
 * ("5 = Completely clear"). On Top N the favourable end also names the points
 * ("Top 2 (4 and 5), 5 = Completely clear"). Null when the rows do not share
 * one scale, or neither end was stamped.
 */
export function divergingLikertScaleKey(
  data: DivergingLikertStoredData,
  view: PlotStatView,
): DivergingLikertScaleKey | null {
  const scale = legendScale(data)
  if (!scale) {
    return null
  }
  const minAnchor = endAnchor(scale, 'min')
  const maxAnchor = endAnchor(scale, 'max')
  if (!minAnchor && !maxAnchor) {
    return null
  }
  const points = view === 'topBoxes' ? data.boxLabels?.top?.trim() || null : null
  const favorableIsMax = favorableIsMaxOf(scale) !== false
  const low = endPhrase(scale.min, minAnchor, points && !favorableIsMax ? points : null)
  const high = endPhrase(scale.max, maxAnchor, points && favorableIsMax ? points : null)
  if (!low && !high) {
    return null
  }
  return { low, high }
}

/** The legend on a view: the three box names on Top N, else Python's band legend. */
export function divergingLikertViewLegend(
  data: DivergingLikertStoredData,
  view: PlotStatView,
): Array<DivergingLikertLegendItem> {
  const scale = legendScale(data)
  if (view === 'topBoxes' && data.boxLabels) {
    return (['bottom', 'middle', 'top'] as const).map((box) => ({
      key: box,
      label: data.boxLabels?.[box] ?? '',
      tone: BOX_TONES[box],
      anchor: boxEndAnchor(box, scale),
    })).filter((item) => item.label.length > 0)
  }
  return data.legend.map((entry) => ({
    key: entry.band,
    label: entry.label,
    tone: entry.band,
    anchor: bandEndAnchor(entry.band, scale),
  }))
}

export interface DivergingLikertCountLegendItem extends DivergingLikertLegendItem {
  /** People in the group, as Python stamped them on the segment (null when not stamped). */
  count: number | null
  /** The group's share of the base, at the payload's decimals. */
  value: number
}

/**
 * The NPS count legend (decision 20: counts always visible): one pooled NPS
 * row, each group with its people and share ("Detractors (0-6): 12 (30%)").
 * Read off the row's stamped segments, so a payload stored before the legend
 * moved here draws the same key. Null on any other read (a rating, several
 * rows): the band legend stands.
 */
export function divergingLikertCountLegend(data: DivergingLikertStoredData): Array<DivergingLikertCountLegendItem> | null {
  if (data.status !== 'ok' || data.scale?.kind !== 'nps' || data.rows.length !== 1) {
    return null
  }
  const scale = pieceScale(data.rows[0], data)
  return data.rows[0].segments.map((segment) => ({
    key: segment.band,
    label: segment.label,
    tone: segment.band,
    anchor: bandEndAnchor(segment.band, scale),
    count: segment.count ?? null,
    value: segment.value,
  }))
}
